import type { ReactElement } from "react";
import { useEffect, useState } from "react";
import { useVersionFileCheck } from "../hooks/useVersionFileCheck";
import { NEW_VERSION_EVENT } from "../utils/lazyWithRetry";
import {
  clearPostVersionReloadNavigationMark,
  isPostVersionReloadNavigation,
  reloadForNewVersion,
} from "../utils/reloadForNewVersion";

type Props = {
  currentVersion: string | undefined;
  url?: string;
};

function handleReloadClick(): void {
  void reloadForNewVersion();
}

export function NewVersionModal({ currentVersion, url }: Props): ReactElement | null {
  const staleFromVersionFile = useVersionFileCheck({ currentVersion, url });
  const [staleFromEvent, setStaleFromEvent] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (isPostVersionReloadNavigation()) {
      clearPostVersionReloadNavigationMark();
    }
  }, []);

  useEffect(() => {
    const handler = (): void => {
      if (isPostVersionReloadNavigation()) return;
      setStaleFromEvent(true);
    };
    window.addEventListener(NEW_VERSION_EVENT, handler);
    return () => window.removeEventListener(NEW_VERSION_EVENT, handler);
  }, []);

  const stale =
    !isPostVersionReloadNavigation() && (staleFromVersionFile || staleFromEvent);

  if (!stale) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-version-title"
      className="fixed inset-0 z-[99] flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <h2 id="new-version-title" className="text-lg font-semibold text-gray-900">
          New version available
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          A new version of the app is available. Reload to get the latest updates.
        </p>
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            disabled={reloading}
            onClick={() => {
              setReloading(true);
              handleReloadClick();
            }}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {reloading ? "Reloading…" : "Reload"}
          </button>
        </div>
      </div>
    </div>
  );
}
